import { useTranslation } from 'react-i18next';
import { ApiTransaction, ApiTransactionPart } from '../openapi';
import {
  formatServerMoney,
  getTransactionDate,
  isMoneyEquals,
  mapCurrencyToFullName,
} from '@razz-team/utils';
import { useMemo } from 'react';

interface GetTransactionDataProps {
  title: string;
  value: string;
  subvalue?: string;
  time: string;
  description?: string | null;
  fee?: string;
  valueState: string;
  valueColor: string;
  details?: {
    Status: string;
    From?: string | null;
    Card?: string | null;
    To?: string | null;
  };
  sender?: {
    'Account owner'?: string | null;
  };
  repeat?: boolean;
}

export enum TransactionTypeDataEnum {
  CARD_ATM_WITHDRAWAL_OUT = 'CARD_ATM_WITHDRAWAL_OUT',
  CARD_ATM_DEPOSIT_IN = 'CARD_ATM_DEPOSIT_IN',
  CARD_PAYMENT_OUT = 'CARD_PAYMENT_OUT',
  OWN_TRANSFER_IN = 'OWN_TRANSFER_IN',
  OWN_TRANSFER_OUT = 'OWN_TRANSFER_OUT',
  INTERNAL_TRANSFER_IN = 'INTERNAL_TRANSFER_IN',
  INTERNAL_TRANSFER_OUT = 'INTERNAL_TRANSFER_OUT',
  EXTERNAL_OUTGOING_TRANSFER_OUT = 'EXTERNAL_OUTGOING_TRANSFER_OUT',
  EXTERNAL_INCOMING_TRANSFER_IN = 'EXTERNAL_INCOMING_TRANSFER_IN',
  EXTERNAL_INCOMING_TRANSFER_IN_OPEN_BANKING = 'EXTERNAL_INCOMING_TRANSFER_IN_OPEN_BANKING',
  EXTERNAL_INCOMING_TRANSFER_IN_ACQUIRING = 'EXTERNAL_INCOMING_TRANSFER_IN_ACQUIRING',
  INTERNAL_OUTGOING_TRANSFER_OUT_VAULT_DEPOSIT = 'INTERNAL_OUTGOING_TRANSFER_OUT_VAULT_DEPOSIT',
  INTERNAL_INCOMING_TRANSFER_IN_VAULT_WITHDRAWAL = 'INTERNAL_INCOMING_TRANSFER_IN_VAULT_WITHDRAWAL',
  REWARD_IN = 'REWARD_IN',
  CASHBACK_IN = 'CASHBACK_IN',
  FEE_OUT = 'FEE_OUT',
  FEE_OUT_CARD_ORDER = 'FEE_OUT_CARD_ORDER',
  FEE_OUT_INVOICE_PAYMENT = 'FEE_OUT_INVOICE_PAYMENT',
}

// TODO: add translations to every string
export function useTransactionDetails<Transaction extends ApiTransaction | undefined>({
  transaction,
}: {
  transaction: Transaction;
}): Transaction extends ApiTransaction
  ? GetTransactionDataProps
  : GetTransactionDataProps | undefined {
  const { t } = useTranslation();

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return useMemo(() => {
    if (!transaction) {
      return undefined;
    }

    function getFeeDescription(part: ApiTransactionPart) {
      const keys = Object.keys(part.fees ?? {});
      return keys
        .map((key) => {
          switch (key) {
            case 'SUBSCRIPTION':
              return t('{{companyName}} Subscription');
            case 'FX':
              return t('Exchange Fee');
            case 'SURCHARGE':
              return t('Surcharge');
            case 'ATM':
              return t('ATM Fee');
            case 'TRANSFER':
              return t('Transfer Fee');
            case 'CARD_ISSUE':
              return t('Card Issue Fee');
            case 'CARD_DELIVERY':
              return t('Card Delivery Fee');
            case 'CARD_MONTHLY_SERVICE':
              return t('Card Monthly Service Fee');
            case 'CARD_FX':
              return t('Card FX Fee');
            case 'CLOSURE':
              return t('Closure Fee');
            case 'DORMANCY':
              return t('Dormancy Fee');
            case 'MARKUP':
              return t('Markup Fee');
          }
        })
        .join(', ');
    }

    const object: GetTransactionDataProps = {
      title: '',
      value:
        transaction.direction === 'OUT'
          ? `-${formatServerMoney(transaction.counterparty.amount)}`
          : `+${formatServerMoney(transaction.origin.amount)}`,
      subvalue:
        transaction.direction === 'OUT'
          ? formatServerMoney(transaction.origin.amount)
          : formatServerMoney(transaction.counterparty.amount),
      valueState: transaction.state,
      valueColor: 'text.secondary',
      description: transaction.description,
      time: getTransactionDate(transaction.date.created, true),
    };

    if (isMoneyEquals(transaction.origin.amount, transaction.counterparty.amount)) {
      object.subvalue = undefined;
    }

    if (transaction.type === TransactionTypeDataEnum.CARD_ATM_WITHDRAWAL_OUT) {
      object.title = `Cash at ${transaction.counterparty.atm}`;
      object.details = {
        Status: transaction.state,
        From: `${transaction.origin.account?.name} account`,
        Card: `••${transaction.origin.card?.lastFour}`,
      };
    }

    if (transaction.type === TransactionTypeDataEnum.CARD_PAYMENT_OUT) {
      object.title = `${transaction.counterparty.merchant?.name ?? 'Cash payment'}`;
      object.details = {
        Status: transaction.state,
        From: `${transaction.origin.account?.name} account`,
        Card: `••${transaction.origin.card?.lastFour}`,
      };
    }

    if (transaction.type === TransactionTypeDataEnum.OWN_TRANSFER_IN) {
      object.title = `From ${transaction.origin.account?.name}`;
      object.details = {
        Status: transaction.state,
        To: `${transaction.counterparty.account?.name} account`,
      };
    }

    if (transaction.type === TransactionTypeDataEnum.OWN_TRANSFER_OUT) {
      object.title = `To ${transaction.counterparty.account?.name}`;
      object.details = {
        Status: transaction.state,
        From: `${transaction.origin.account?.name} account`,
        To: transaction.counterparty.account?.name,
      };
    }

    if (transaction.type === TransactionTypeDataEnum.INTERNAL_TRANSFER_OUT) {
      object.title = t('To {{name}}', {
        name: transaction.counterparty?.customer?.name ?? t('{{companyName}} user'),
      });
      object.details = {
        Status: transaction.state,
        From: `${transaction.origin.account?.name} account`,
      };
      if (transaction.state === 'COMPLETED') {
        object.repeat = true;
      }
    }

    if (transaction.type === TransactionTypeDataEnum.INTERNAL_TRANSFER_IN) {
      object.title = `From ${transaction.origin.customer?.name}`;
      object.details = {
        Status: transaction.state,
        To: `${transaction.counterparty.account?.name} account`,
      };
    }

    if (transaction.type === TransactionTypeDataEnum.EXTERNAL_INCOMING_TRANSFER_IN) {
      if (transaction.origin.payer?.type === 'CRYPTO') {
        object.title = `From ${transaction.origin.payer.accountNumber?.value}`;
        object.details = {
          Status: transaction.state,
          To: `${transaction.counterparty.account?.name} account`,
        };
        object.sender = {
          'Account owner': transaction.origin.payer.accountNumber?.value,
        };
      } else {
        object.title = `From ${transaction.origin.payer?.payerName}`;
        object.details = {
          Status: transaction.state,
          To: `${transaction.counterparty.account?.name} account`,
        };
        object.sender = {
          'Account owner': transaction.origin.payer?.payerName,
        };
      }
    }

    if (transaction.type === TransactionTypeDataEnum.EXTERNAL_INCOMING_TRANSFER_IN_ACQUIRING) {
    }

    if (transaction.type === TransactionTypeDataEnum.EXTERNAL_OUTGOING_TRANSFER_OUT) {
      if (transaction.counterparty.beneficiary?.type === 'INDIVIDUAL') {
        object.title = `To ${transaction.counterparty.beneficiary.individualName}`;
        object.details = {
          Status: transaction.state,
          From: `${transaction.origin.account?.name} account`,
        };
      }
      if (transaction.counterparty.beneficiary?.type === 'BUSINESS') {
        object.title = `To ${transaction.counterparty.beneficiary.businessName}`;
        object.details = {
          Status: transaction.state,
          From: `${transaction.origin.account?.name} account`,
        };
      }
      if (transaction.counterparty.beneficiary?.type === 'CRYPTO') {
        object.title = `To ${
          transaction.counterparty.beneficiary.displayName
            ? transaction.counterparty.beneficiary.displayName
            : `${mapCurrencyToFullName(transaction.counterparty.amount.currency)} wallet`
        }`;
        object.details = {
          Status: transaction.state,
          From: `${transaction.origin.account?.name} account`,
        };
      }
      if (transaction.state === 'COMPLETED') {
        object.repeat = true;
      }
    }

    if (transaction.type === TransactionTypeDataEnum.REWARD_IN) {
      object.title = `To ${transaction.counterparty.account?.name}`;
      object.details = {
        Status: transaction.state,
        From: t('{{companyName}} App'),
      };
    }

    if (transaction.type === TransactionTypeDataEnum.FEE_OUT) {
      object.title = t('Fee');
      object.details = {
        Status: transaction.state,
        From: transaction.origin.account?.name,
      };
    }

    if (transaction.type === TransactionTypeDataEnum.FEE_OUT_CARD_ORDER) {
      object.title = t('Card Order Fee');
      object.details = {
        Status: transaction.state,
        From: transaction.origin.account?.name,
      };
    }

    if (transaction.type === TransactionTypeDataEnum.FEE_OUT_INVOICE_PAYMENT) {
      object.title = getFeeDescription(transaction.origin);
      object.details = {
        Status: transaction.state,
        From: t('{{companyName}}'),
      };
    }

    if (transaction.type.includes('VAULT') || transaction.type.includes('OWN_TRANSFER')) {
      const from =
        transaction.origin.card?.name ??
        (transaction.origin.vault ? t('Vault') : undefined) ??
        transaction.origin.account?.name ??
        transaction.counterparty.customer?.name ??
        t('Unknown');
      const to =
        transaction.counterparty.card?.name ??
        (transaction.counterparty.vault ? t('Vault') : undefined) ??
        transaction.counterparty.account?.name ??
        transaction.counterparty.customer?.name ??
        t('Unknown');
      object.title = `${from} → ${to}`;
    }

    switch (transaction.state) {
      case 'FAILED':
      case 'PENDING':
        object.valueColor = 'text.secondary';
        break;
      default:
        if (transaction.direction === 'IN') {
          object.valueColor = 'text.success';
        } else {
          object.valueColor = 'text.primary';
        }
        break;
    }

    return object;
  }, [transaction, t]);
}
